// Date-range picker for the raffle. A single trigger (the whole field, not a
// tiny icon) opens a custom calendar popover matching the page's honey/glass
// look — click a start day, then an end day; hovering previews the range.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isToday,
  isBefore,
  isAfter,
  format,
} from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { enUS } from 'date-fns/locale/en-US';
import { ro } from 'date-fns/locale/ro';
import { RAFFLE_NS } from './translations';
import { CalendarIcon, ChevronDownIcon } from './icons';

const LOCALES = { ru, en: enUS, ro };
const WEEKDAYS = {
  ru: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
  ro: ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};

const parseValue = (value) => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DateRangePicker = ({ startValue, endValue, onChangeRange, invalid = false }) => {
  const { t, i18n } = useTranslation(RAFFLE_NS);
  const activeLang = i18n.language?.startsWith('ru') ? 'ru' : i18n.language?.startsWith('ro') ? 'ro' : 'en';
  const locale = LOCALES[activeLang] || LOCALES.en;
  const weekDayLabels = WEEKDAYS[activeLang] || WEEKDAYS.en;

  const [open, setOpen] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);
  const rootRef = useRef(null);

  const startDate = useMemo(() => parseValue(startValue), [startValue]);
  const endDate = useMemo(() => parseValue(endValue), [endValue]);

  const [viewMonth, setViewMonth] = useState(() => startOfMonth(startDate || new Date()));

  const close = useCallback(() => setOpen(false), []);

  const openPopover = useCallback(() => {
    setViewMonth(startOfMonth(startDate || new Date()));
    setOpen(true);
  }, [startDate]);

  // Close on outside click / Esc while open.
  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(monthStart);
    const s = startOfWeek(monthStart, { weekStartsOn: 1 });
    const e = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: s, end: e });
  }, [viewMonth]);

  // Any click either starts a fresh range (no start yet, or both bounds
  // already set) or completes the pending one — picking a day before the
  // current start just restarts the range from there instead of swapping.
  const handleDayClick = (day) => {
    if (!startDate || endDate) {
      onChangeRange(toValue(day), '');
      return;
    }
    if (isBefore(day, startDate)) {
      onChangeRange(toValue(day), '');
      return;
    }
    onChangeRange(toValue(startDate), toValue(day));
    setOpen(false);
  };

  const handleClear = () => {
    onChangeRange('', '');
    setHoverDate(null);
  };

  const previewEnd =
    startDate && !endDate && hoverDate && !isBefore(hoverDate, startDate) ? hoverDate : null;
  const rangeEnd = endDate || previewEnd;

  const triggerLabel = useMemo(() => {
    if (startDate && endDate) {
      return `${format(startDate, 'd MMM yyyy', { locale })} — ${format(endDate, 'd MMM yyyy', { locale })}`;
    }
    if (startDate) {
      return `${format(startDate, 'd MMM yyyy', { locale })} — …`;
    }
    return t('dateRange.placeholder');
  }, [startDate, endDate, locale, t]);

  return (
    <div className={`rf-daterange${invalid ? ' rf-daterange--invalid' : ''}`} ref={rootRef}>
      <span className="rf-daterange__label">
        <CalendarIcon size={15} />
        {t('dateRange.label')}
      </span>

      <button
        type="button"
        className={`rf-daterange__trigger${open ? ' is-open' : ''}${startDate ? ' has-value' : ''}`}
        onClick={() => (open ? close() : openPopover())}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="rf-daterange__value">{triggerLabel}</span>
        <ChevronDownIcon size={17} className="rf-daterange__chevron" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="rf-daterange__popover"
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="rf-daterange__cal-header">
              <button
                type="button"
                className="rf-daterange__nav rf-daterange__nav--prev"
                onClick={() => setViewMonth((m) => subMonths(m, 1))}
                aria-label="Previous month"
              >
                <ChevronDownIcon size={15} />
              </button>
              <span className="rf-daterange__month">
                {format(viewMonth, 'LLLL yyyy', { locale })}
              </span>
              <button
                type="button"
                className="rf-daterange__nav rf-daterange__nav--next"
                onClick={() => setViewMonth((m) => addMonths(m, 1))}
                aria-label="Next month"
              >
                <ChevronDownIcon size={15} />
              </button>
            </div>

            <div className="rf-daterange__weekdays">
              {weekDayLabels.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="rf-daterange__grid" onMouseLeave={() => setHoverDate(null)}>
              {calendarDays.map((day) => {
                const inMonth = isSameMonth(day, viewMonth);
                const isStart = startDate && isSameDay(day, startDate);
                const isEnd = endDate && isSameDay(day, endDate);
                const isPreviewEnd = previewEnd && isSameDay(day, previewEnd);
                const inRange =
                  startDate &&
                  rangeEnd &&
                  isAfter(day, startDate) &&
                  isBefore(day, rangeEnd);
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    className={[
                      'rf-daterange__day',
                      !inMonth && 'is-dim',
                      isToday(day) && 'is-today',
                      (isStart || isEnd) && 'is-bound',
                      isPreviewEnd && 'is-preview',
                      inRange && 'is-in-range',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => handleDayClick(day)}
                    onMouseEnter={() => setHoverDate(day)}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>

            <div className="rf-daterange__footer">
              <span className="rf-daterange__hint">
                {startDate && !endDate ? t('dateRange.placeholderPartial') : ' '}
              </span>
              {(startDate || endDate) && (
                <button type="button" className="rf-daterange__clear" onClick={handleClear}>
                  {t('dateRange.clear')}
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {invalid && <p className="rf-daterange__error">{t('dateRange.invalid')}</p>}
    </div>
  );
};

export default DateRangePicker;
